import { SLIDER_CENTER, SLIDER_MAX, TOTAL_PROBLEMS } from './constants.js';
import { updateLevelButtons } from './levels.js';
import { getMaxErrorForLevel, getPerfectErrorForLevel, getTotalScore, updateTotalScoreDisplay } from './scoring.js';
import { dom, scoreBrackets } from './dom.js';
import { generateScenario } from './scenario.js';
import { resetMeasurement, syncActionButtons } from './measurement.js';
import { disableExplanationScrollLayout } from './layout.js';
import { updateViewAndDraw } from './radar.js';
import { state } from './state.js';
import { updateSliderDisplay } from './slider.js';

function getPassedLevelsFromScores(scoresByLevel) {
    return Object.entries(scoresByLevel)
        .filter(([, score]) => score > 0)
        .map(([level]) => Number(level));
}

export function startGame(options = {}) {
    const {
        mode = 'practice',
        dailyRun = null,
        dailyCompleted = false,
        timeRun = null,
        currentProblem = 1,
        createScenarioRandom = null,
        scenarioOptions = null,
        scoresByLevel = {},
        submittedLevels = [],
    } = options;

    state.gameMode = mode;
    state.dailyRun = dailyRun;
    state.dailyCompleted = dailyCompleted;
    state.timeRun = timeRun;
    state.createScenarioRandom = createScenarioRandom;
    state.scenarioOptions = scenarioOptions;
    state.currentProblem = currentProblem;
    state.submittedLevels = new Set(submittedLevels.map(Number));
    state.passedLevels = new Set(getPassedLevelsFromScores(scoresByLevel));
    state.scoresByLevel = { ...scoresByLevel };
    dom.scoreLabel.textContent = mode === 'time' ? 'Correct' : 'Total Score';
    dom.dailyChallengeBtn.classList.toggle('game-header__daily-btn--active', mode === 'daily');
    dom.timeChallengeBtn.classList.toggle('game-header__daily-btn--active', mode === 'time');
    if (mode !== 'time') {
        dom.timeStatus.hidden = true;
        dom.timerProgress.classList.add('hidden');
    }
    dom.summaryModal.classList.add('hidden');
    loadProblem();
}

export function goToLevel(level) {
    if (level < 1 || level > TOTAL_PROBLEMS) return;
    state.currentProblem = level;
    loadProblem();
}

export function selectLevel(level) {
    if (state.gameMode === 'daily' || state.gameMode === 'time') return;
    goToLevel(level);
}

export function replayLevel() {
    if (state.gameMode === 'daily' || state.gameMode === 'time') return;
    loadProblem();
}

export function loadProblem() {
    updateTotalScoreDisplay();
    updateLevelButtons();

    resetMeasurement();
    dom.slider.disabled = false;
    dom.slider.classList.remove('submitted');
    dom.slider.value = SLIDER_CENTER;
    updateSliderDisplay(SLIDER_CENTER);
    dom.resultTextEl.textContent = '';
    dom.playerGuessMarker.classList.add('hidden');
    dom.correctAnswerMarker.classList.add('hidden');
    scoreBrackets().forEach((b) => b.classList.add('hidden'));
    dom.explanationContainer.classList.add('hidden');
    disableExplanationScrollLayout();

    const rng = typeof state.createScenarioRandom === 'function'
        ? state.createScenarioRandom(state.currentProblem)
        : Math.random;
    generateScenario(state.currentProblem, rng, state.scenarioOptions || undefined);
    updateViewAndDraw();
    syncActionButtons();
}

export function showSummary() {
    updateLevelButtons();
    dom.finalScoreEl.textContent = getTotalScore();
    dom.scoreBreakdownListEl.innerHTML = '';

    for (let level = 1; level <= TOTAL_PROBLEMS; level++) {
        const li = document.createElement('li');
        const score = state.scoresByLevel[level];
        if (score === undefined) {
            li.innerHTML = `Lvl ${level}: <span class="text-gray-500">—</span>`;
        } else {
            const color = score > 0 ? 'text-green-400' : 'text-gray-400';
            li.innerHTML = `Lvl ${level}: <span class="font-bold ${color}">${score > 0 ? '+' : ''}${score}</span>`;
        }
        dom.scoreBreakdownListEl.appendChild(li);
    }

    dom.summaryModal.classList.remove('hidden');
}

export function maybeShowSummary() {
    if (state.gameMode === 'practice' && state.submittedLevels.size >= TOTAL_PROBLEMS) {
        showSummary();
    }
}

export function showAnswerFeedback(rawValue) {
    const maxError = getMaxErrorForLevel(state.currentProblem);
    const perfectError = getPerfectErrorForLevel(state.currentProblem);

    dom.playerGuessMarker.style.left = `${(rawValue / SLIDER_MAX) * 100}%`;
    dom.playerGuessMarker.classList.remove('hidden');

    const correctRawValue =
        state.solution.firstToCross === state.aircraft1.callsign
            ? SLIDER_CENTER - state.solution.minDistance
            : SLIDER_CENTER + state.solution.minDistance;
    dom.correctAnswerMarker.style.left = `${(correctRawValue / SLIDER_MAX) * 100}%`;
    dom.correctAnswerMarker.classList.remove('hidden');

    const rawPerfectL = correctRawValue - perfectError;
    const rawPerfectR = correctRawValue + perfectError;
    const rawMaxL = correctRawValue - maxError;
    const rawMaxR = correctRawValue + maxError;

    dom.bracketPerfectL.style.left = `calc(${(rawPerfectL / SLIDER_MAX) * 100}% - 4px)`;
    dom.bracketPerfectR.style.left = `calc(${(rawPerfectR / SLIDER_MAX) * 100}% - 4px)`;
    dom.bracketMaxL.style.left = `calc(${(rawMaxL / SLIDER_MAX) * 100}% - 4px)`;
    dom.bracketMaxR.style.left = `calc(${(rawMaxR / SLIDER_MAX) * 100}% - 4px)`;
    scoreBrackets().forEach((b) => b.classList.remove('hidden'));
}

export function parseSliderAnswer(rawValue) {
    const userAnswerDist =
        rawValue <= SLIDER_CENTER ? SLIDER_CENTER - rawValue : rawValue - SLIDER_CENTER;
    const userAnswerFirst =
        rawValue < SLIDER_CENTER ? state.aircraft1.callsign : state.aircraft2.callsign;

    if (userAnswerFirst === state.solution.firstToCross) {
        return Math.abs(userAnswerDist - state.solution.minDistance);
    }
    return userAnswerDist + state.solution.minDistance;
}
